import { useQuery, UseQueryResult, UseQueryOptions } from 'react-query'
import { getAvailabilitiesToValidateSelectedSlot } from 'src/services/api/availabilities'
import captureExceptionWithResponse from 'src/services/sentry'
import { Availabilities } from '@customTypes/Availabilities'
import { PractitionerReferenceId } from '@customTypes/Practitioner'
import { PatientTypeOptions } from 'src/config/patientTypeOptions'
import { useRecallQueryParams } from '../useQueryParams'
import { getPractitionerReferenceIds } from 'src/utils/getPractitionerReferenceId'
import { useConfig } from '@context/ConfigurationContext'
import { useChoiceState } from '@context/ChoiceContext'

const staleTime = 900000 // 15 minutes in miliseconds

type UseAvailabilitiesInput = {
  preferredPractitionerReferenceIds?: PractitionerReferenceId[]
  start?: string
  end?: string
  disabled?: boolean
}

/**
 * @deprecated This is only used in recall to validate if slot exists. Please use src/react/hooks/useAvailabilities/useAvailabilities.ts instead
 */
const useAvailabilities = (
  options: UseAvailabilitiesInput,
  queryOptions?: UseQueryOptions<unknown, any, Availabilities[], any>
): UseQueryResult<Availabilities[]> => {
  const { clinicId } = useConfig()
  const { queryParams: recallQueryParams } = useRecallQueryParams()
  const { insuranceType } = useChoiceState()
  const treatmentBundleTypeReferenceId = recallQueryParams?.treatmentBundleTypeReferenceId
  const hasRecallQueryParams =
    Boolean(treatmentBundleTypeReferenceId) && Object.keys(recallQueryParams ?? {}).length > 0
  const start = options.start ? new Date(options.start).toISOString() : undefined // From QueryParams, in Berlin Timezone
  const end = options.end
  const patientType = PatientTypeOptions.OLD
  const customerId = recallQueryParams?.customerId
  const preferredPractitionerReferenceIds = options?.preferredPractitionerReferenceIds

  const recallAvailabilities = useQuery(
    [
      'recallAvailabilities',
      clinicId,
      treatmentBundleTypeReferenceId,
      getPractitionerReferenceIds(preferredPractitionerReferenceIds),
      patientType,
      start,
      customerId,
      end,
    ],
    () =>
      getAvailabilitiesToValidateSelectedSlot({
        clinicReferenceId: clinicId,
        treatmentReferenceId: treatmentBundleTypeReferenceId ?? '',
        practitionerReferenceIds: getPractitionerReferenceIds(preferredPractitionerReferenceIds),
        patientType,
        start,
        end,
        customerId,
        insuranceType,
      }),
    {
      enabled: options?.disabled ? false : hasRecallQueryParams,
      staleTime,
      refetchOnWindowFocus: true,
      ...queryOptions,
    }
  )

  let response = recallAvailabilities

  if (response.isError) {
    captureExceptionWithResponse('Something went wrong while fetching appointments', response.error)
  }

  return response
}

export default useAvailabilities
